import React, { useState } from 'react'
import { Store, Layers, ShoppingCart, TrendingUp, Building2, Code2, BarChart3, Rss, Shapes } from 'lucide-react'
import BottomMenu from './BottomMenu'
import OptionCard from './OptionCard'

// #693: the business-type value persisted to user_account.account_type. Mirrors
// the payhelm.com/onboard "What best describes your business?" taxonomy. Store /
// AI-first routing is NOT derived from this — it follows the chosen path + whether
// a store is connected — this is descriptive metadata to tailor the platform.
export type AccountType =
  | 'b2c_brand'
  | 'b2b_brand'
  | 'retailer'
  | 'marketplace_seller'
  | 'agency'
  | 'tool_provider'
  | 'consultant'
  | 'publisher_affiliate'
  | 'other'

interface BusinessTypeStepProps {
  initialValue?: AccountType | null
  onNext: (value: AccountType) => void
  onBack?: () => void
}

interface Option {
  value: AccountType
  title: string
  description: string
  icon: React.ReactNode
  tile: string
}

const OPTIONS: Option[] = [
  { value: 'b2c_brand', title: 'B2C Brand / Merchant', description: 'A direct-to-consumer brand or merchant selling on owned channels and marketplaces', icon: <Store className="tw-h-5 tw-w-5" />, tile: 'tw-bg-indigo-50 tw-text-indigo-600' },
  { value: 'b2b_brand', title: 'B2B Brand / Wholesaler', description: 'A brand selling to retailers, distributors, or other businesses via wholesale or B2B channels', icon: <Layers className="tw-h-5 tw-w-5" />, tile: 'tw-bg-blue-50 tw-text-blue-600' },
  { value: 'retailer', title: 'Retailer / Brick & Mortar', description: 'A physical or omnichannel retailer with in-store and online sales', icon: <ShoppingCart className="tw-h-5 tw-w-5" />, tile: 'tw-bg-orange-50 tw-text-orange-600' },
  { value: 'marketplace_seller', title: 'Marketplace Seller / Reseller', description: 'A seller listing and reselling products on Amazon, eBay, Walmart, or similar marketplaces', icon: <TrendingUp className="tw-h-5 tw-w-5" />, tile: 'tw-bg-rose-50 tw-text-rose-600' },
  { value: 'agency', title: 'Agency', description: 'An agency managing analytics, ads, or operations across multiple client brands', icon: <Building2 className="tw-h-5 tw-w-5" />, tile: 'tw-bg-purple-50 tw-text-purple-600' },
  { value: 'tool_provider', title: 'E-commerce Tool Provider', description: 'A SaaS, app, or plugin company building products for e-commerce merchants', icon: <Code2 className="tw-h-5 tw-w-5" />, tile: 'tw-bg-green-50 tw-text-green-600' },
  { value: 'consultant', title: 'Consultant / Advisor', description: 'A strategy or advisory firm serving e-commerce brands and operators', icon: <BarChart3 className="tw-h-5 tw-w-5" />, tile: 'tw-bg-amber-50 tw-text-amber-600' },
  { value: 'publisher_affiliate', title: 'Publisher / Affiliate', description: 'A content site, media brand, or affiliate monetizing traffic through product recommendations', icon: <Rss className="tw-h-5 tw-w-5" />, tile: 'tw-bg-teal-50 tw-text-teal-600' },
  { value: 'other', title: 'Other', description: "Something else — we'll tailor the platform once you're set up", icon: <Shapes className="tw-h-5 tw-w-5" />, tile: 'tw-bg-slate-100 tw-text-slate-600' },
]

/** #693: "What best describes your business?" — right-panel content (chrome + progress supplied by the container). */
const BusinessTypeStep: React.FC<BusinessTypeStepProps> = ({ initialValue = null, onNext, onBack }) => {
  const [selected, setSelected] = useState<AccountType | null>(initialValue)

  return (
    <div className="tw-p-4 md:tw-p-8 tw-pb-32">
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
        {OPTIONS.map((o) => (
          <OptionCard
            key={o.value}
            horizontal
            selected={selected === o.value}
            onClick={() => setSelected(o.value)}
            icon={o.icon}
            tile={o.tile}
            title={o.title}
            description={o.description}
          />
        ))}
      </div>
      <BottomMenu
        onPrev={onBack}
        onNext={() => selected && onNext(selected)}
        disableNext={!selected}
      />
    </div>
  )
}

export default BusinessTypeStep
